import React, { useCallback, useEffect, useState } from "react";
import { api } from "../lib/api";
import { AdminLayout } from "../components/AdminLayout";
import { Button } from "../components/ui/button";
import { Bell, Loader2, MessageSquare, Save } from "lucide-react";
import { toast } from "sonner";
import { FormField } from "../components/ui/form-field";
import { useI18n } from "../i18n/I18nContext";
import { logError } from "../lib/logger";

const EVENT_INFO_KEYS = {
  confirmation: { icon: "📅", descKey: "Disparada quando um cliente é cadastrado ou uma nova limpeza é agendada." },
  reminder: { icon: "⏰", descKey: "Disparada 1 dia antes da limpeza agendada como lembrete." },
  started: { icon: "🏠", descKey: 'Disparada quando o colaborador clica em "START" no app mobile.' },
  finished: { icon: "✅", descKey: "Disparada quando a limpeza é finalizada (envia também o PDF do comprovante quando disponível)." },
};

const PLACEHOLDER_DEFS = [
  { key: "{cliente}", descKey: "Nome do cliente" },
  { key: "{data}", descKey: "Data da limpeza (MM/DD/AAAA)" },
  { key: "{hora}", descKey: "Horário da limpeza (ex: 10:00 AM)" },
  { key: "{equipe}", descKey: "Nome da equipe responsável" },
];

export default function NotificationSettings() {
  const { t } = useI18n();
  const [settings, setSettings] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/notification-settings");
      setSettings(data);
    } catch (e) {
      logError(e);
      toast.error(t("Erro ao carregar configurações"));
    } finally {
      setBusy(false);
    }
  }, [t]);

  useEffect(() => {
    load();
  }, [load]);

  async function save() {
    setSaving(true);
    try {
      await api.put("/notification-settings", settings);
      toast.success(t("Configurações salvas"));
    } catch (e) {
      toast.error(t("Erro ao salvar"));
    } finally {
      setSaving(false);
    }
  }

  if (busy || !settings) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center py-20 text-slate-400">
          <Loader2 className="animate-spin mr-2" /> {t("Carregando…")}
        </div>
      </AdminLayout>
    );
  }

  function patch(key, field, value) {
    setSettings({
      ...settings,
      [key]: { ...settings[key], [field]: value },
    });
  }

  function buildPreview(template) {
    return template
      .replaceAll("{cliente}", "Anna Smith")
      .replaceAll("{data}", "06/05/2026")
      .replaceAll("{hora}", "10:00 AM")
      .replaceAll("{equipe}", "Time 02");
  }

  return (
    <AdminLayout>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-blue-700 font-semibold">
            {t("Comunicação")}
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold flex items-center gap-2">
            <Bell size={26} /> {t("Notificações ao cliente")}
          </h1>
          <div className="text-sm text-slate-500 mt-1">
            {t("Configure templates de mensagem para WhatsApp/SMS. As mensagens são enviadas via link direto wa.me/sms — você confirma o envio com 1 clique no app do WhatsApp.")}
          </div>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          data-testid="save-notif-settings-btn"
          className="bg-blue-600 hover:bg-blue-700"
        >
          {saving ? (
            <Loader2 size={16} className="mr-1 animate-spin" />
          ) : (
            <Save size={16} className="mr-1" />
          )}
          {t("Salvar configurações")}
        </Button>
      </div>

      {/* Placeholders helper */}
      <div className="cm-card p-4 mb-6 bg-blue-50 border-blue-200">
        <div className="text-xs font-semibold text-blue-900 mb-2">
          {t("Variáveis disponíveis nos templates:")}
        </div>
        <div className="flex flex-wrap gap-2">
          {PLACEHOLDER_DEFS.map((p) => (
            <span
              key={p.key}
              className="px-2 py-1 rounded-md bg-white border border-blue-200 text-xs text-blue-800"
              title={t(p.descKey)}
            >
              <code className="font-mono">{p.key}</code>{" "}
              <span className="text-blue-500">— {t(p.descKey)}</span>
            </span>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        {Object.entries(settings).map(([key, cfg]) => {
          const info = EVENT_INFO_KEYS[key];
          if (!info) return null;
          return (
            <div
              key={key}
              data-testid={`notif-card-${key}`}
              className={`cm-card overflow-hidden border-2 transition-colors ${
                cfg.enabled
                  ? "border-blue-200"
                  : "border-slate-200 opacity-75"
              }`}
            >
              <div className="flex items-start justify-between p-4 border-b border-slate-100 bg-slate-50">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{info.icon}</span>
                    <div>
                      <div className="font-semibold text-slate-900">
                        {t(cfg.label)}
                      </div>
                      <div className="text-xs text-slate-500">
                        {t(info.descKey)}
                      </div>
                    </div>
                  </div>
                </div>
                <label className="inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!!cfg.enabled}
                    onChange={(e) => patch(key, "enabled", e.target.checked)}
                    data-testid={`notif-toggle-${key}`}
                    className="sr-only peer"
                  />
                  <div className="relative w-11 h-6 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600" />
                </label>
              </div>

              <div className="p-4 space-y-3">
                <FormField label={t("Template da mensagem")}>
                  <textarea
                    rows={3}
                    className="cm-input font-mono text-sm"
                    value={cfg.template}
                    onChange={(e) => patch(key, "template", e.target.value)}
                    data-testid={`notif-template-${key}`}
                    placeholder={t("Digite a mensagem...")}
                  />
                </FormField>
                <div className="p-3 rounded-lg bg-emerald-50 border border-emerald-200">
                  <div className="text-xs font-semibold text-emerald-700 mb-1 flex items-center gap-1">
                    <MessageSquare size={12} /> {t("Preview")}
                  </div>
                  <div className="text-sm text-slate-800 whitespace-pre-wrap">
                    {buildPreview(cfg.template)}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="cm-card p-4 mt-6 bg-amber-50 border-amber-200">
        <div className="text-sm text-amber-900">
          <strong>{t("Como usar:")}</strong> {t("Após salvar, vá em uma limpeza, clique no botão")}{" "}
          <span className="font-mono bg-amber-100 px-1 rounded">📱 {t("Notificar cliente")}</span>{" "}
          {t("e o WhatsApp/SMS abrirá com a mensagem do template já preenchida com os dados do cliente. Basta clicar enviar.")}
        </div>
      </div>
    </AdminLayout>
  );
}
